import { useMemo } from 'react'
import { FlowNode } from '@integration-app/sdk'

import { useFlowNode } from 'components/FlowBuilder/context/flow-node-context'
import useFlowVariables from 'components/FlowBuilder/useFlowVariables'
import { useGenericFlow } from 'components/FlowBuilder/useGenericFlow'
import { useDeepMemo } from 'hooks/useDeepEffect'
import {
  ConfigurationErrorType,
  NodeConfigurationErrorData,
  NodeConfigurationErrorsGetter,
} from 'routes/Workspaces/Workspace/routes/flows/Flow/Build/SidePanels/nodes/configuration-errors/types'
import { CONSOLE_NODE_SPECS } from 'routes/Workspaces/Workspace/routes/flows/Flow/Build/SidePanels/nodes/index'

export function useConfigurationErrors() {
  const { flow } = useGenericFlow()

  const calculationDependencies = JSON.stringify([flow?.nodes ?? {}])

  const nodes = flow.nodes ?? {}

  const { getNodeRunTimeVariablesSchema, getNodeInputSchema } =
    useFlowVariables()

  const nodesErrors: Record<string, NodeConfigurationErrorData[]> =
    useMemo(() => {
      const result = {}

      for (const key in nodes) {
        const node = nodes[key]

        if (!node?.type) {
          // allowed in universal flow
          if (!flow.universalFlowId) {
            continue
          }

          result[key] = [getMissingTypeError()]
          continue
        }
        if (!CONSOLE_NODE_SPECS[node?.type]) {
          result[key] = [getInvalidTypeError(node)]
          continue
        }

        const config = node.config ?? {}

        const runTimeVariablesSchema = getNodeRunTimeVariablesSchema(key)
        const inputSchema = getNodeInputSchema(key)

        const getters: NodeConfigurationErrorsGetter[] =
          CONSOLE_NODE_SPECS[node.type]?.errorsGetters ?? []

        result[key] = getters
          .map((getter) =>
            getter?.({
              flow,
              node,
              nodeKey: key,
              config,
              runTimeVariablesSchema,
              inputSchema,
            }),
          )
          .flat()

        if ((node as any).errors) {
          result[key].push(...(node as any).errors)
        }
      }

      return result
    }, [calculationDependencies])

  function getNodeErrors(key: string): NodeConfigurationErrorData[] {
    return nodesErrors[key] ?? []
  }

  function getNodeErrorsFieldLocators(key: string) {
    return getNodeErrors(key)
      .map(({ valueLocator }) => valueLocator)
      .filter(Boolean)
  }

  const nodesWithErrors = useMemo(() => {
    const result = {}

    for (const key in nodesErrors) {
      if (nodesErrors[key].length > 0) {
        result[key] = nodes[key]
      }
    }

    return result
  }, [calculationDependencies])

  return useDeepMemo(
    () => ({
      getNodeErrorsFieldLocators,
      getNodeErrors,
      nodesWithErrors,
      nodesErrors,
    }),
    [nodesErrors, nodesWithErrors],
  )
}

export function useNodeConfigurationErrors() {
  const { nodeKey } = useFlowNode()
  const { getNodeErrors, getNodeErrorsFieldLocators } = useConfigurationErrors()

  return {
    errors: getNodeErrors(nodeKey),
    errorFieldsLocators: getNodeErrorsFieldLocators(nodeKey),
  }
}

function getMissingTypeError(): NodeConfigurationErrorData {
  return {
    type: ConfigurationErrorType.MissingType,
    message: 'Node type is missing',
  }
}

function getInvalidTypeError(node: FlowNode): NodeConfigurationErrorData {
  return {
    type: ConfigurationErrorType.InvalidType,
    message: `Invalid node type: ${node?.type}`,
  }
}
